import { useCallback, useState } from 'react';
import { useUpdateCaseClosingConfigurationMutation } from 'store/api/tenants';
import { CaseClosingConfigurationDto, CaseClosingConfigurationUpdateDto } from 'types/caseClosing';
import { toCaseClosingUpdate } from './caseClosingConfirmation';

type CaseClosingChanges = Partial<CaseClosingConfigurationUpdateDto>;

export function useCaseClosingSubsectionEdit(saved: CaseClosingConfigurationDto) {
    const [updateCaseClosingConfiguration, { isLoading: isSaving }] = useUpdateCaseClosingConfigurationMutation();
    const [isEditMode, setIsEditMode] = useState(false);
    const [changes, setChanges] = useState<CaseClosingChanges>({});

    const startEditing = useCallback(() => {
        setChanges({});
        setIsEditMode(true);
    }, []);

    const cancel = useCallback(() => {
        setChanges({});
        setIsEditMode(false);
    }, []);

    const change = useCallback((next: CaseClosingChanges) => setChanges((current) => ({ ...current, ...next })), []);

    const valueOf = useCallback(
        <K extends keyof CaseClosingConfigurationUpdateDto>(key: K): CaseClosingConfigurationUpdateDto[K] =>
            changes[key] ?? saved[key],
        [changes, saved]
    );

    // The API middleware toasts both outcomes; a failed save keeps the subsection in edit mode.
    const save = useCallback(
        async (extraChanges: CaseClosingChanges = {}) => {
            try {
                await updateCaseClosingConfiguration(
                    toCaseClosingUpdate(saved, { ...changes, ...extraChanges })
                ).unwrap();
                setChanges({});
                setIsEditMode(false);
            } catch (e) {
                console.error('Case closing configuration update failed', e);
            }
        },
        [changes, saved, updateCaseClosingConfiguration]
    );

    return { isEditMode, isSaving, startEditing, cancel, change, valueOf, save };
}
